/**
 * SFrame138 – IAD Step 4: Funeral or Cremation Services
 * Standardized Checklist UX following the exact pattern of Frame 137 & 124.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProgressHeader from '../settlement/ProgressHeader';
import ChecklistItem from '../settlement/ChecklistItem'; 
import { useSettlementSession } from '../../hooks/useSettlementSession';
import {
  loadResidencePanelCompletion,
  withEstatePath,
} from '../../utils/secureResidenceFlow';
import { 
  PlannerDataSection, 
  PlannerDataField 
} from '../settlement/PlannerDataSection';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';

interface FrameDataWithNotes {
  completed?: boolean;
  notes?: string;
}

const PHASE = 'iad';
const STEP_ID = 'iad-4';
const OVERVIEW_ROUTE = '/sframe-141';
const isPlanner = true;

const containerClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.container : '';
const contentColumnClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.contentColumn : '';
const titleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.title : 'font-display text-[28px] leading-tight text-blue-900';
const subtitleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.subtitle : 'text-sm text-grey-600 mb-6 leading-relaxed';

const FUNERAL_PANELS = [
  { frameId: 'frame-funeral-provider', title: 'Select Funeral Home or Provider', intro: 'Choose and contact a funeral home or cremation provider.' },
  { frameId: 'frame-funeral-type', title: 'Confirm Service Type', intro: 'Decide burial, cremation, or other arrangements.' },
  { frameId: 'frame-funeral-plan', title: 'Schedule and Plan the Service', intro: 'Set the date, location, and key details.' },
  { frameId: 'frame-funeral-notify', title: 'Notify Family and Community', intro: 'Inform people and share service details.' },
  { frameId: 'frame-funeral-costs', title: 'Review Costs and Payments', intro: 'Understand costs and how they will be covered.' },
];

const CHECKLIST_GUIDANCE = [
  {
    what: 'Identify who will handle arrangements.',
    why: 'They guide the process and manage logistics.',
    actions: ['Check for pre-arranged plans or preferences', 'Contact 1–2 providers to compare options', 'Confirm availability and services offered', 'Share basic information about the deceased'],
  },
  {
    what: 'Choose how the deceased will be laid to rest.',
    why: 'Impacts timing, cost, and service planning.',
    actions: ['Review any written wishes or family input', 'Decide burial, cremation, or alternative', 'Confirm with provider', 'Align with religious or cultural preferences'],
  },
  {
    what: 'Plan when and where the service will take place.',
    why: 'Allows people to attend and preparations to begin.',
    actions: ['Select date, time, and location', 'Confirm officiant or speaker', 'Plan basic elements (music, readings, format)', 'Coordinate transportation if needed'],
  },
  {
    what: 'Let people know about the passing and service.',
    why: 'Ensures key people are informed and can attend.',
    actions: ['Notify immediate family and close contacts first', 'Share service details once confirmed', 'Consider obituary or announcement', 'Track key attendees if helpful'],
  },
  {
    what: 'Get clarity on expenses and payment approach.',
    why: 'Avoids surprises and ensures costs are handled properly.',
    actions: ['Request estimate from provider', 'Check for prepaid plans or insurance', 'Identify who will pay initially', 'Keep a simple record of costs'],
  },
];

// Helper for original Planner Data rendering
function pickString(obj: Record<string, unknown> | undefined, key: string): string {
  if (!obj) return '';
  const v = obj[key];
  return typeof v === 'string' ? v : v != null ? String(v) : '';
}

function renderPlanner223Summary(data: Record<string, unknown>) {
  const fp = (data.funeralPreferences || data.funeral_preferences) as Record<string, unknown> | undefined;
  const md = (data.memorialDetails || data.memorial_details) as Record<string, unknown> | undefined;

  return (
    <div className="space-y-4 text-sm text-stone-800">
      {fp && (
        <div>
          <h4 className="font-semibold text-stone-900 mb-1">Funeral preferences</h4>
          <PlannerDataField label="Service style" value={pickString(fp, 'serviceType')} />
          <PlannerDataField label="Disposition" value={pickString(fp, 'burialType')} />
          <PlannerDataField label="Special requests" value={pickString(fp, 'specialRequests')} />
        </div>
      )}
      {md && (
        <div className="pt-2 border-t border-stone-100">
          <h4 className="font-semibold text-stone-900 mb-1">Memorial details</h4>
          <PlannerDataField label="Music" value={pickString(md, 'musicPreferences')} />
          <PlannerDataField label="Readings" value={pickString(md, 'readings')} />
          <PlannerDataField label="Donations" value={pickString(md, 'donations')} />
        </div>
      )}
    </div>
  );
}

export default function SFrame138() {
  const { estateId: paramEstateId } = useParams<{ estateId: string }>();
  const estateId = paramEstateId || null;
  const navigate = useNavigate();
  
  const { error, loadFrameData, loadPhaseProgress, updateStepProgress, saveFrameData } = useSettlementSession(estateId);

  const [completedPanels, setCompletedPanels] = useState(0);
  const [panelDone, setPanelDone] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');

  const refresh = useCallback(async () => {
    if (!estateId) return;

    // Load checklist completion
    const { byFrameId } = await loadResidencePanelCompletion(estateId, loadFrameData);
    const funeralFrameIds = FUNERAL_PANELS.map(p => p.frameId);
    const count = Object.keys(byFrameId).filter(id => funeralFrameIds.includes(id) && byFrameId[id]).length;
    
    setCompletedPanels(count);
    setPanelDone(byFrameId);

    // Load notes
    const frameData = await loadFrameData(STEP_ID) as FrameDataWithNotes;
    if (frameData?.notes) setNotes(frameData.notes);
  }, [estateId, loadFrameData]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleToggleComplete = async (frameId: string, newState: boolean) => {
    const newPanelDone = { ...panelDone, [frameId]: newState };
    setPanelDone(newPanelDone);
    
    const funeralFrameIds = FUNERAL_PANELS.map(p => p.frameId);
    setCompletedPanels(Object.keys(newPanelDone).filter(id => funeralFrameIds.includes(id) && newPanelDone[id]).length);

    if (estateId) {
      await saveFrameData(frameId, { completed: newState });
      await refresh();
      
      const steps = await loadPhaseProgress(PHASE);
      const row = steps.find((s) => s.stepId === STEP_ID);
      if (row?.status === 'not-started' || !row) {
        await updateStepProgress(STEP_ID, PHASE, { status: 'in-progress' });
      }
    }
  };

  const handleNoteChange = async (val: string) => {
    setNotes(val);
    if (estateId) {
      await saveFrameData(STEP_ID, { notes: val } as Record<string, unknown>);
    }
  };

  return (
    <div className={containerClassName}>
      <div className={contentColumnClassName}>
        {error && (
          <div className="mb-4 bg-orange-100 border border-orange-300 rounded-md p-3 text-orange-900 text-sm max-w-3xl mx-auto">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => navigate(withEstatePath(OVERVIEW_ROUTE, estateId))}
            className="w-[35px] h-[35px] flex items-center justify-center text-grey-600 hover:text-blue-900 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className={titleClassName}>Funeral or Cremation Services</h1>
        </div>

        <p className={subtitleClassName}>
          Plan and coordinate arrangements to honor the deceased and bring people together.
        </p>

        <div className="mb-8">
          <PlannerDataSection
            estateId={estateId ?? ''}
            plannerFrameId="frame-223"
            title="Memorial preferences from estate plan"
            emptyMessage="No preferences were saved in the estate plan (Frame 223)."
            renderData={renderPlanner223Summary}
          />
        </div>

        <ProgressHeader
          title="Plan funeral or cremation"
          variant={isPlanner ? 'planner' : 'default'}
          subtitle="Work through each item to organize the service and notify those who need to know."
          completedSteps={completedPanels}
          totalSteps={FUNERAL_PANELS.length}
          estimatedMinutes={60}
        />

        <div className="space-y-4 mt-8">
          {FUNERAL_PANELS.map((panel, index) => {
            const guidance = CHECKLIST_GUIDANCE[index];
            return (
              <ChecklistItem
                key={panel.frameId}
                id={panel.frameId}
                title={panel.title}
                description={panel.intro}
                whatThisIs={guidance.what}
                whyItMatters={guidance.why}
                bulletPoints={guidance.actions}
                checked={!!panelDone[panel.frameId]}
                onChange={handleToggleComplete}
              />
            );
          })}
        </div>

        {/* --- Notes Section --- */}
        <div className="mt-10 pt-10 border-t border-stone-200">
          <h3 className="text-lg font-semibold text-stone-900 mb-2">Notes</h3>
          <textarea
            className="w-full rounded-lg border border-stone-300 p-4 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all min-h-[120px]"
            placeholder="Add details, locations, or reminders to keep track of your progress here"            
            value={notes}
            onChange={(e) => handleNoteChange(e.target.value)}
          />
        </div>

      </div>
    </div>
  );
}